/**
 * Main contract template.
 *
 * SOURCE OF TRUTH: HCA-CONTRACT-v2-TEMPLATE.md in
 * kv_backend_latest/docs/projects/auhca-portal/research/.
 *
 * This file is GENERATED — do not hand-edit. To update:
 *   1. Edit the markdown source in the AUHCA repo
 *   2. Run scripts/auhcaSyncContractTemplate.js
 *   3. Re-deploy / restart bandn
 */
module.exports = {
  TEMPLATE_VERSION: '2.0.0-2026-06-19',
  TEMPLATE_MD: `# EMPLOYMENT CONTRACT

**Between:**
**{{employerName}}** (the **Employer**)

**And:**
**{{candidateFullName}}** of **{{candidateAddress}}**{{candidateIdClause}} (the **Employee**)

Dated **{{contractDate}}**.

## 1. Parties and Background

1.1 The Employer operates a residential aged care service in {{employerState}}, Australia.

1.2 The Employer wishes to employ the Employee in the position described in clause 2, and the Employee accepts that employment, on the terms set out in this contract.

1.3 The Employer is an approved aged care provider for the purposes of the Aged Care Act 1997 (Cth) (and, from its commencement, the Aged Care Act 2024 (Cth)) and (where applicable) is a party to the Aged Care Industry Labour Agreement (ACILA) with the Department of Home Affairs.

## 2. Position and Duties

2.1 The Employee is employed in the position of **{{positionTitle}}** ({{anzscoCode}} {{anzscoTitle}}).

2.2 The Employee's duties are those set out in the position description attached as **Schedule A**, together with any other duties reasonably directed by the Employer that are within the Employee's skill, competence and training and consistent with the nominated occupation.

2.3 The Employee reports to **{{employerContactName}}** or such other person as the Employer may notify in writing.

2.4 The Employee acknowledges that the role is a genuine, ongoing position and that the duties described match the nominated ANZSCO occupation for the purposes of any sponsored visa nomination.

## 3. Commencement and Term

3.1 The Employee's employment commences on **{{commencementDate}}** and continues until terminated in accordance with clause 14.

3.2 This is a **{{employmentType}}** engagement. Where the Employee is sponsored under the Aged Care Industry Labour Agreement, the engagement is full-time and the Employee will work a minimum of 30 ordinary hours per week.

## 4. Location

4.1 The Employee's primary place of work is **{{primaryWorksite}}**, {{employerState}}.

4.2 The Employer may reasonably require the Employee to work at any other facility operated by the Employer within reasonable travelling distance, on reasonable notice.

## 5. Hours of Work

5.1 The Employee's ordinary hours of work are **{{ordinaryHoursPerWeek}}** per week, averaged over a roster cycle in accordance with the Aged Care Award 2010 (MA000018).

5.2 The Employee may be required to work reasonable additional hours having regard to the matters in section 62 of the Fair Work Act 2009 (Cth).

5.3 Rosters will be set by the Employer in accordance with MA000018. Minimum engagement is 4 hours per shift for permanent employees and 2 hours per engagement for casual employees.

5.4 Shift loadings, weekend penalty rates, public holiday rates and overtime rates are paid in addition to base salary in accordance with MA000018.

5.5 **Right to Disconnect.** Consistent with section 333M of the Fair Work Act 2009 (Cth), the Employee may refuse to monitor, read or respond to contact, or attempted contact, from the Employer outside the Employee's working hours unless the refusal is unreasonable. Operational requirements such as on-call rostering, urgent resident welfare matters and Serious Incident Response Scheme reporting are recognised as reasonable contact.

## 6. Remuneration

6.1 The Employee will be paid an annual base salary of **AUD {{salaryAnnual}}** (equivalent to approximately **AUD {{salaryHourly}} per ordinary hour**), classified at **Direct Care Level {{classificationLevel}}** under MA000018.

6.2 The base salary in clause 6.1 is at or above the higher of (a) the minimum rate payable under MA000018 for the Employee's classification, (b) the Annual Market Salary Rate for an Australian citizen or permanent resident performing equivalent work at the same location, and (c) any applicable income threshold under the Employee's visa stream.

6.3 The base salary in clause 6.1 does not include shift loadings, weekend penalties, public holiday penalties, overtime, or any allowance payable under MA000018. Those amounts are paid in addition.

6.4 The Employer will pay the Employee fortnightly into a bank account nominated by the Employee. The Employee will be issued a payslip within one working day of each payday.

6.5 The Employer will review the base salary at least once each year, with effect from the first full pay period on or after 1 July, to ensure it remains at or above the higher of the applicable award rate, the Annual Market Salary Rate, and any applicable visa income threshold.

## 7. Superannuation

7.1 The Employer will make superannuation contributions on the Employee's behalf at the rate prescribed by the Superannuation Guarantee (Administration) Act 1992 (Cth), being 12% of Ordinary Time Earnings from 1 July 2025.

7.2 Contributions will be paid to a complying superannuation fund. The Employee may nominate a fund by completing the Standard Choice Form. If no fund is nominated, the Employer will direct contributions to the Employee's stapled fund (or the Employer's default fund, where no stapled fund exists).

## 8. Leave

8.1 **Annual leave.** The Employee accrues 4 weeks paid annual leave per year of service (or 5 weeks for 7-day shiftworkers within the meaning of MA000018), with 17.5% leave loading, in accordance with the National Employment Standards and MA000018.

8.2 **Personal/carer's leave.** The Employee accrues 10 days paid personal/carer's leave per year of service. A further 2 days unpaid carer's leave per permissible occasion is available.

8.3 **Compassionate leave.** 2 days paid compassionate leave per permissible occasion.

8.4 **Parental leave.** Up to 12 months unpaid parental leave with a right to request a further 12 months, in accordance with the National Employment Standards.

8.5 **Paid family and domestic violence leave.** 10 days paid per year.

8.6 **Community service leave.** Unpaid generally; up to 10 days paid jury service leave.

8.7 **Long service leave.** Per the Long Service Leave Act 1955 (NSW) (or the corresponding legislation in the State or Territory of the Employee's primary worksite).

8.8 **Aboriginal and Torres Strait Islander ceremonial leave.** Up to 10 unpaid working days per year per MA000018.

8.9 **Public holidays.** The Employee is entitled to a paid day off on a public holiday gazetted in the State of the Employee's primary worksite, unless reasonably requested to work, in which case public holiday penalty rates apply under MA000018.

## 9. Probation

9.1 The Employee's employment is subject to a probation period of **{{probationMonths}} months** from the commencement date.

9.2 During probation either party may terminate the employment by giving 1 week's written notice (or payment in lieu).

9.3 For clarity, the statutory minimum employment period for unfair dismissal protection under section 382 of the Fair Work Act 2009 (Cth) is 6 months (or 12 months for small business employers with fewer than 15 employees).

## 10. Code of Conduct, Quality Standards and Policies

10.1 The Employee will comply at all times with the **Aged Care Code of Conduct** under the Aged Care Quality and Safety Commission Act 2018 (Cth), the **Aged Care Quality Standards** (Standards 1 to 8), and all applicable Employer policies and procedures.

10.2 The Employee will comply with the **Serious Incident Response Scheme (SIRS)** and notify senior staff of any reportable incident as soon as possible.

10.3 The Employee will treat all residents with dignity and respect, act with integrity, deliver care safely and competently, raise quality and safety concerns promptly, and refrain from any violence, discrimination, exploitation, neglect, abuse or sexual misconduct.

10.4 Breach of the Code of Conduct, the Quality Standards, or any Employer policy may result in disciplinary action up to and including summary dismissal, and may also attract civil penalties enforceable by the Aged Care Quality and Safety Commission.

## 11. Pre-Employment and Ongoing Screening

11.1 The Employee's employment is conditional on, and continues subject to, the Employee at all times holding:

(a) a valid **Nationally Coordinated Criminal History Check** (police check) renewed at least every 3 years, with no precluding offence within the meaning of the Aged Care Act 1997 (Cth);

(b) where applicable, a valid **NDIS Worker Screening Check** clearance;

(c) where the role involves child-related work as a usual feature, a valid **Working With Children Check** for the relevant State or Territory;

(d) Australian work rights (citizenship, permanent residence, or a valid visa with permission to work);

(e) any qualifications, registrations and competencies required for the role, including (where applicable) Certificate III in Individual Support (Ageing) or equivalent positive skills assessment.

11.2 The Employee must notify the Employer in writing within 7 days of any conviction, charge, suspension, cancellation or other event that would or might affect any of the matters in clause 11.1.

11.3 Where required for entry to the Employee's worksite, the Employee will (where lawful in the relevant State or Territory) comply with the Employer's immunisation policy, including current influenza vaccination. COVID-19 vaccination is encouraged but not mandated by the Employer except where required by law. The Employer maintains a documented exemption and redeployment process.

## 12. Training Obligations

12.1 The Employee will obtain and maintain at the Employee's own cost (unless the Employer agrees otherwise in writing):

(a) HLTAID011 Provide First Aid (3-year validity) with annual HLTAID009 CPR re-certification;

(b) where the role involves assisting with medication, HLTHPS006 Assist Clients with Medication or equivalent;

(c) any other training reasonably required by the Employer including manual handling, infection control, dementia care, elder abuse awareness and SIRS awareness.

12.2 The Employee will attend all mandatory training and inductions arranged by the Employer.

## 13. Work Health and Safety

13.1 The Employer will take all reasonably practicable steps to provide a safe workplace, including mechanical lifting aids and a no-lift policy consistent with the Work Health and Safety Act 2011 (NSW) (or corresponding State legislation).

13.2 The Employee will take reasonable care for their own health and safety and that of others, comply with any reasonable instruction given by the Employer to comply with WHS laws, and report all hazards, incidents and injuries promptly.

## 14. Termination and Notice

14.1 Either party may terminate this contract by giving the notice period prescribed by section 117 of the Fair Work Act 2009 (Cth) and MA000018, whichever is the greater. As at the date of this contract the statutory minimum scale is:

- less than 1 year service: 1 week;
- 1 to 3 years: 2 weeks;
- 3 to 5 years: 3 weeks;
- 5 or more years: 4 weeks;
- plus an additional week if the Employee is over 45 and has at least 2 years' continuous service.

14.2 The Employer may terminate the Employee summarily without notice for serious misconduct, including any conduct that is grounds for summary dismissal at common law or under the Fair Work Regulations 2009 (Cth).

14.3 On termination the Employee will be paid all accrued but untaken annual leave and any other entitlements payable under the National Employment Standards and MA000018.

14.4 The Employee acknowledges that, where the Employee is sponsored on a Skills in Demand subclass 482 visa, cessation of employment triggers a 28-day notification obligation on the Employer to the Department of Home Affairs, and that the Employee may use the post-cessation grace period (up to 180 consecutive days per cessation, capped at 365 days across the visa) to find a new sponsor.

## 15. Confidentiality

15.1 The Employee must not, during or after employment, use or disclose any confidential information of the Employer, of any resident, or of any third party to whom the Employer owes a duty of confidence, except as required by law or with the prior written consent of the Employer.

15.2 Confidential information includes resident records, care plans, clinical information, commercial information, financial information, business strategies and any other information that is not in the public domain.

15.3 This clause is in addition to, and does not limit, any statutory confidentiality obligations under the Privacy Act 1988 (Cth), the Health Records and Information Privacy Act 2002 (NSW), the Aged Care Act 1997 (Cth) and any successor legislation.

## 16. Intellectual Property

16.1 All intellectual property created by the Employee in the course of employment vests in the Employer on creation, consistent with section 35(6) of the Copyright Act 1968 (Cth).

16.2 The Employee will do all things reasonably necessary to perfect the Employer's ownership of that intellectual property.

## 17. Visa and Sponsorship Acknowledgments

17.1 Where the Employee holds a visa sponsored by the Employer, the Employee acknowledges that:

(a) the nominated position is a genuine, ongoing role;

(b) the Employee will perform the duties of the nominated ANZSCO occupation;

(c) the Employer will not pass on or recoup any sponsorship, nomination, Skilling Australians Fund (SAF) levy or recruitment costs from the Employee;

(d) the Employer will, on written request, pay reasonable and necessary return travel costs (residence to departure point plus economy airfare to the Employee's home country) within 30 days, in accordance with sponsorship obligations under Division 2.19 of the Migration Regulations 1994;

(e) the Employee will keep the Employer informed of any change of address or visa status within 14 days.

17.2 The Employer is not a registered migration agent. Any immigration advice should be obtained from a registered migration agent or Australian legal practitioner.

## 18. Governing Law and Jurisdiction

18.1 This contract is governed by the laws of **{{employerState}}** and the Commonwealth of Australia.

18.2 The parties submit to the non-exclusive jurisdiction of the courts of {{employerState}}.

## 19. Entire Agreement, Variation and Counterparts

19.1 This contract, together with the Schedules, constitutes the entire agreement between the parties and supersedes any prior representations.

19.2 Any variation must be in writing and signed by both parties.

19.3 This contract may be executed in counterparts, including by electronic signature in accordance with the Electronic Transactions Act 1999 (Cth). Each counterpart is an original and together they constitute one document.

## 20. National Employment Standards and Modern Award Acknowledgment

20.1 This contract operates subject to, and does not seek to exclude, the National Employment Standards under the Fair Work Act 2009 (Cth) and the Aged Care Award 2010 (MA000018). Where any term of this contract is less favourable than the NES or MA000018, the NES or MA000018 prevails to the extent of the inconsistency.

20.2 The Employer has provided, or will provide before or as soon as practicable after commencement:

(a) the **Fair Work Information Statement** under section 125 of the Fair Work Act 2009 (Cth);

(b) where the Employee is engaged on a casual basis, the **Casual Employment Information Statement**;

(c) where the Employee is engaged on a fixed-term basis, the **Fixed Term Contract Information Statement**.

## 21. Schedules

The following schedules are incorporated by reference into this contract and form part of it. The Employee acknowledges that they have read or have had an opportunity to read each of them before signing.

- **Schedule A: Position Description.** Available at {{positionDescriptionUrl}}
- **Schedule B: Fair Work Information Statement.** Published by the Fair Work Ombudsman and available at https://www.fairwork.gov.au/employment-conditions/national-employment-standards/fair-work-information-statement
- **Schedule C: Aged Care Code of Conduct.** Published by the Aged Care Quality and Safety Commission and available at {{codeOfConductUrl}}

## Execution

**Signed for and on behalf of the Employer**

Name: **{{employerContactName}}**
Position: **{{employerContactPosition}}**
Signature: {{employerSignatureBlock}}
Date: **{{signedAt}}**

**Signed by the Employee**

Name: **{{candidateFullName}}**
Signature: {{candidateSignatureBlock}}
Date: **{{signedAt}}**

---`,
};
